// React Query hook for the user's credit balance.
//
// Refetches on window focus — if the user bought credits in another tab
// (Polar checkout opens in a new window by default), we want the sidebar
// to reflect the new balance as soon as they tab back. Stale time 30s so
// we don't hammer the server during rapid navigation.

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getBalance, type CreditBalance } from "./api";

export const BALANCE_QUERY_KEY = ["billing-balance"] as const;

export function useBalance(): {
  balance: CreditBalance | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
} {
  const q = useQuery({
    queryKey: BALANCE_QUERY_KEY,
    queryFn: getBalance,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    retry: 1,
  });
  return {
    balance: q.data ?? null,
    isLoading: q.isLoading,
    error: (q.error as Error | null) ?? null,
    refetch: () => void q.refetch(),
  };
}

/** Invalidate the balance cache so the sidebar re-reads. Call after
 *  any action that charges credits (build-deck, playtest, ask) or
 *  after top-up/subscription webhook lands. */
export function useRefreshBalance(): () => void {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: BALANCE_QUERY_KEY });
  };
}
